import { ArrowDownRight, ArrowUpRight } from 'lucide-react'
import type { ReactNode } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

type Tint = 'primary' | 'success' | 'warning' | 'info' | 'accent'

const tintCls: Record<Tint, string> = {
  primary: 'bg-primary/12 text-primary',
  success: 'bg-success/12 text-success',
  warning: 'bg-warning/15 text-warning',
  info: 'bg-info/12 text-info',
  accent: 'bg-accent text-accent-foreground',
}

export function KpiCard({
  label,
  value,
  hint,
  trend,
  trendUp,
  icon,
  tint = 'primary',
  className,
  action,
}: {
  label: string
  value: ReactNode
  hint?: string
  trend?: string
  trendUp?: boolean
  icon?: ReactNode
  tint?: Tint
  className?: string
  action?: ReactNode
}) {
  return (
    <Card className={cn('rounded-2xl border-border/70 shadow-sm', className)}>
      <CardContent className="flex items-start justify-between gap-2 p-4 sm:gap-3 sm:p-5">
        <div className="min-w-0 space-y-1.5 sm:space-y-2">
          <div className="text-xs font-medium text-muted-foreground sm:text-[13px]">{label}</div>
          <div className="truncate text-2xl leading-none font-semibold tracking-tight tabular-nums sm:text-[28px]">{value}</div>
          {trend ? (
            <div
              className={cn(
                'inline-flex items-center gap-0.5 text-xs font-medium',
                trendUp === false ? 'text-destructive' : 'text-success',
              )}
            >
              {trendUp === false ? <ArrowDownRight className="size-3.5" /> : <ArrowUpRight className="size-3.5" />}
              {trend}
            </div>
          ) : null}
          {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
          {action}
        </div>
        {icon ? (
          <div className={cn('flex size-8 shrink-0 items-center justify-center rounded-lg sm:size-11 sm:rounded-2xl [&_svg]:size-4 sm:[&_svg]:size-5', tintCls[tint])}>
            {icon}
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}
